import {
	Home,
	UsersRound,
	ShoppingCart,
	MapPin,
	Radio,
	type LucideIcon
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMemo } from 'react'

import { useAuthStore } from '../stores/auth.store'

interface MenuItem {
	id: string
	label: string
	icon: LucideIcon
	path: string
	action?: () => void
	badge?: number | string
	disabled?: boolean
}

interface BottomMenuItemProps extends MenuItem {
	isSelected: boolean
	onClick: () => void
}

function BottomMenuItem({
	icon: Icon,
	label,
	isSelected,
	onClick,
	badge,
	disabled = false
}: BottomMenuItemProps) {
	return (
		<button
			onClick={onClick}
			disabled={disabled}
			className={`
        relative flex flex-1 flex-col items-center justify-center py-2 px-1
        transition-all duration-300 ease-out
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-95'}
        ${isSelected
					? 'text-red-500'
					: 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
				}
      `}
			aria-label={label}
			role="tab"
			aria-selected={isSelected}
		>
			{/* Background indicator */}
			<div className={`
        absolute inset-0 rounded-xl transition-all duration-300
        ${isSelected
					? 'bg-red-50 dark:bg-red-900/20 scale-100'
					: 'bg-transparent scale-95 hover:bg-gray-50 dark:hover:bg-gray-800/50'
				}
      `} />

			{/* Icon container */}
			<div className="relative z-10 mb-1">
				<Icon
					size={22}
					className={`
            transition-all duration-300
            ${isSelected ? 'scale-110' : 'scale-100 group-hover:scale-105'}
          `}
				/>

				{/* Badge */}
				{badge && (
					<div className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
						{typeof badge === 'number' && badge > 99 ? '99+' : badge}
					</div>
				)}
			</div>

			{/* Label */}
			<span className={`
        text-xs font-medium transition-all duration-300 text-center leading-tight
        ${isSelected ? 'scale-105' : 'scale-100'}
      `}>
				{label}
			</span>

			{/* Active indicator */}
			<div className={`
        absolute bottom-0 left-1/2 transform -translate-x-1/2 h-1 rounded-full
        transition-all duration-300 bg-red-500
        ${isSelected ? 'w-8 opacity-100' : 'w-0 opacity-0'}
      `} />
		</button>
	)
}

export function BottomMenuComponent() {
	const navigate = useNavigate()
	const location = useLocation()
	const nivelAcesso = useAuthStore((state) => state.nivelAcesso)

	// Get company code from localStorage with fallback
	const companyCode = useMemo(() => {
		if (typeof window !== 'undefined') {
			return localStorage.getItem('companyCode') || 'defaultCompany'
		}
		return 'defaultCompany'
	}, [])

	// Companies that show routes instead of orders (RESTAURADO)
	const companiesRouteMenu = ['994', '369']
	const hasRoutes = companiesRouteMenu.includes(companyCode)

	// Menu configuration
	const menuItems = useMemo((): MenuItem[] => [
		{
			id: 'home',
			label: 'Home',
			icon: Home,
			path: '/',
		},
		{
			id: 'customers',
			label: 'Clientes',
			icon: UsersRound,
			path: '/clientes',
		},
		{
			id: 'orders-routes',
			label: hasRoutes ? 'Rotas' : 'Pedidos',
			icon: hasRoutes ? MapPin : ShoppingCart,
			path: hasRoutes ? '/rotas' : '/pedidos',
			// badge: hasRoutes ? undefined : 3, // Example badge for orders
		},
		{
			id: 'broadcast',
			label: 'Transmitir',
			icon: Radio,
			path: '/transmitir',
		},
	], [hasRoutes])

	const handleItemClick = (item: MenuItem) => {
		if (item.disabled) return

		if (item.action) {
			item.action()
		} else {
			navigate(item.path)
		}
	}

	// Check if current path matches item path
	const isItemSelected = (item: MenuItem) => {
		if (item.path === '/') {
			return location.pathname === '/'
		}
		return location.pathname.startsWith(item.path)
	}

	if (nivelAcesso === 3) return null

	return (
		<>
			{/* Spacer for fixed menu */}
			<div className="h-[70px] lg:hidden" />

			{/* Bottom Menu */}
			<nav
				className="
          fixed bottom-0 left-0 right-0 z-40 pb-2 lg:hidden
          bg-white/80 dark:bg-gray-900/80 backdrop-blur-md
          border-t border-gray-200 dark:border-gray-700
          shadow-lg
        "
				role="tablist"
				aria-label="Menu de navegação principal"
			>
				<div className="flex items-center justify-around h-[70px] px-2 max-w-md mx-auto">
					{menuItems.map((item) => (
						<BottomMenuItem
							key={item.id}
							{...item}
							isSelected={isItemSelected(item)}
							onClick={() => handleItemClick(item)}
						/>
					))}
				</div>

				{/* Safe area padding for devices with home indicator */}
				<div className="h-safe-area-inset-bottom" />
			</nav>
		</>
	)
}